import { digger } from '@cbre/digger';
import {
  concatValuesBy,
  genThumbMediaURI,
  getImageByType,
  getPrimaryImage,
  getPrimarySecureImage,
  isMediaImage,
  simpleValueValidation,
} from '@miq/utils';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocaleId } from '../../../../../models/locale.model';
import { ReportRecord } from '../../../../../models/report.model';
import { DisplayFunctionOptions, LovValues, TemplateFieldsDisplayFn } from '../../../../../models/template.model';
import { MapHandlerOptions } from '../../../../../models/windward-blueprint.model';
import { UTC_EDP_INPUT_FORMAT } from '../../../../constants/date.const';
import { getRegularOrColloquialVal } from '../../../template-lov-attributes.const';
import { getMediaInformation } from '../../../templates-formatter.const';
import { sortAttributesFields } from '../../../utils/argument-sorting';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../../utils/dynamic-data-retrievers';
import { getDisclaimerByLocale } from '../../common';
import { currencyLov, PropertyType, valueWithUomPrefix } from '../../us/us.common';

function immediatelyTranslation(locale: LocaleId): string {
  let result: string;
  switch (locale) {
    case LocaleId.ItalyItalian:
      result = 'Immediata';
      break;
    case LocaleId.PolandPolish:
      result = 'Natychmiast';
      break;
    case LocaleId.FinlandFinnish:
      result = 'Heti';
      break;
    case LocaleId.NorwayNorwegian:
      result = 'Umiddelbart';
      break;
    case LocaleId.SwedenSwedish:
      result = 'Omedelbart';
      break;
    default:
      result = 'Immediately';
      break;
  }
  return result;
}

export const displayFnMap: TemplateFieldsDisplayFn = {
  98: {
    /*
     * NOTE:(edmundo): we were supposed to use 98 & 99 for the availability images
     * and 528 & 527 for property images. However, due to existing images logic
     * implemented in the report-field-formatter.service, we cannot use 98 & 99 to
     * return the custom data and data structure we need for each availability.
     *
     * Therefore, 98 & 99 will be used for property images & 528 & 527 for the
     * availabilities images
     */
    // 98 - Primary Image
    displayFn: (record: ReportRecord) => {
      const mediaData = record.associatedProperty
        ? record.associatedProperty.getMediaInformation()
        : record.data.getMediaInformation();
      return (mediaData || []).filter(media => {
        return isMediaImage(media);
      });
    },
  },
  99: {
    // 99 - Additional Image
    displayFn: (record: ReportRecord) => {
      const mediaData = record.associatedProperty
        ? record.associatedProperty.getMediaInformation()
        : record.data.getMediaInformation();
      return (mediaData || []).filter(media => {
        return isMediaImage(media);
      });
    },
  },
  263: {
    // 263 - floor plan image
    displayFn: (record: ReportRecord) => {
      const mediaInformation = getMediaInformation(record);
      return (mediaInformation || []).filter(media => {
        return isMediaImage(media);
      });
    },
  },
  266: {
    // 266 - summary map
    displayFn: (_record: ReportRecord, options) => {
      const { reportId } = options;
      const edpRecordId = undefined;
      const mapOptions: MapHandlerOptions = {
        enableCache: true,
        isGroupByProperty: true,
        width: 1200,
        height: 810,
      };
      return of([reportId, edpRecordId, mapOptions]);
    },
  },
  349: {
    // 349 - Address (word report)
    displayFn: (record: ReportRecord, options, ...args) => {
      const { displayFnArgumentsOrder } = options;
      const finalArgs = sortAttributesFields(args, displayFnArgumentsOrder);
      const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      return concatValuesBy(', ', ...values);
    },
    routeFn: ({ data }) => {
      const entityRecord = Array.isArray(data) ? data[0] : data;
      return ['/records', 'availability', digger(entityRecord, 'entity.id'), 'edit'];
    },
  },
  399: {
    // 399 - description
    displayFn: displayFnWrapper,
  },
  395: {
    // 395 - status
    displayFn: (record: ReportRecord, _, status) => {
      return status.lovValues.pipe(
        map((lovs: LovValues[]) => {
          const lovKeyValue = mapAndFormatDynamicValue(record, status, lovs);
          const lovValue = lovs.find(a => a.key === lovKeyValue);
          return lovValue ? lovValue.value : '';
        })
      );
    },
  },
  393: {
    // 393 - Class (this is field 356 in shortlist/longlist)
    displayFn: (record: ReportRecord, _, classArg) => {
      return classArg.lovValues.pipe(
        map((lovs: LovValues[]) => {
          const lovKeyValue = mapAndFormatDynamicValue(record, classArg, lovs);
          const lovValue = lovs.find(a => a.key === lovKeyValue);
          return lovValue ? lovValue.value : '-';
        })
      );
    },
  },
  396: {
    // 396 - Average Direct Asking Rate Yearly
    displayFn: (record: ReportRecord, options, averageDirectArg) => {
      return currencyLov(options).pipe(
        map((currencyLOV: LovValues[]) => {
          const value = valueWithUomPrefix(record, PropertyType.Office, averageDirectArg, currencyLOV);
          const valueFromArray = Array.isArray(value) && value.length > 0 ? value[0] : '-';
          return Array.isArray(value) ? valueFromArray : value;
        })
      );
    },
  },
  398: {
    // 398 - Min - Max Direct Asking Rate Monthly
    displayFn: (record: ReportRecord, options, askingRateMin, askingRateMax) => {
      return currencyLov(options).pipe(
        map((currencyLOV: LovValues[]) => {
          const minRate = valueWithUomPrefix(record, PropertyType.Office, askingRateMin, currencyLOV);
          const maxRate = valueWithUomPrefix(record, PropertyType.Office, askingRateMax, currencyLOV);
          return concatValuesBy(' - ', minRate, maxRate);
        })
      );
    },
  },
  397: {
    // 397 - Service Charge
    displayFn: (record: ReportRecord, options, serviceCharge) => {
      return currencyLov(options).pipe(
        map((currencyLOV: LovValues[]) => {
          const value = valueWithUomPrefix(record, PropertyType.Office, serviceCharge, currencyLOV);
          return Array.isArray(value) ? value.join(', ') : value;
        })
      );
    },
  },
  391: {
    // 391 - min leased term
    displayFn: (record: ReportRecord) => {
      const availabilities = record.data || [];
      const numericValues = availabilities
        .map(availability => {
          const value = (availability || {}).minumum_lease_term;
          const numericValue = Number(value);
          return isNaN(numericValue) ? null : numericValue;
        })
        .filter(min => min != null);
      return numericValues.length > 0 ? Math.min(...numericValues).toString() : '-';
    },
  },
  392: {
    // 392 - Parking Space
    displayFn: (record: ReportRecord, options, parkingSpaceArg) => {
      const value = displayFnWrapper(record, options, parkingSpaceArg);
      return Array.isArray(value) ? value.join(', ') : value;
    },
  },
  401: {
    // 401 - Offered Space
    displayFn: (record: ReportRecord, options: DisplayFunctionOptions) => {
      const { locale, reportExportViewService } = options;
      const propertyUsagesLOV$ = reportExportViewService.getLovObservablesData(
        `/lookup/ref/country?lookupcategory=miq_property_usage`,
        undefined,
        locale
      ) as Observable<LovValues[]>;

      return propertyUsagesLOV$.pipe(
        map(propertyUsagesLOV => {
          const availabilities = record.data;

          const offeredSpace = availabilities.map(availability => {
            const floor = digger(availability, 'floor_suite.floor_number') || '-';
            const usageKey = digger(availability, 'property.property_usage.ref_property_usage_type_desc');
            const usageVal = propertyUsagesLOV.find(a => a.key === usageKey);
            const usage = usageVal ? usageVal.value : '-';

            const sqm = availability.available_space || '-';
            const availabilityDate = moment(availability.date_available, UTC_EDP_INPUT_FORMAT);
            const isValid = availabilityDate.isValid();

            let available = '-';
            if (isValid) {
              const now = moment();
              const isSameYear = availabilityDate.isSame(now, 'year');
              const isSameMonth = availabilityDate.isSame(now, 'month');
              const isSameDay = availabilityDate.isSame(now, 'day');
              const isBefore = availabilityDate.isBefore(now);
              const isTodayOrBefore = (isSameYear && isSameMonth && isSameDay) || isBefore;
              available = isTodayOrBefore
                ? immediatelyTranslation(options.locale)
                : availabilityDate.format('DD/MM/YYYY');
            }

            return { floor, usage, sqm, available };
          });

          const sortedOfferedSpace = [];
          const unsortedOfferedSpace = [];
          for (const space of offeredSpace) {
            const { floor } = space;
            if (!isNaN(Number(floor))) {
              sortedOfferedSpace.push(space);
            } else {
              unsortedOfferedSpace.push(space);
            }
          }

          sortedOfferedSpace.sort((a, b) => {
            return Number(b.floor) - Number(a.floor);
          });
          unsortedOfferedSpace.sort((a, b) => a.floor.localeCompare(b.floor));

          return [...sortedOfferedSpace, ...unsortedOfferedSpace];
        })
      );
    },
  },
  366: {
    // 366 - property name
    displayFn: displayFnWrapper,
  },
  417: {
    // 367 - Floor Label
    displayFn: displayFnWrapper,
  },
  402: {
    // 368 - Usage Label
    displayFn: displayFnWrapper,
  },
  403: {
    // 369 - Sqm label
    displayFn: displayFnWrapper,
  },
  404: {
    // 370 - Available Label
    displayFn: displayFnWrapper,
  },
  394: {
    // 394 - year renovated
    displayFn: (record: ReportRecord, options, yearRenoArg) => {
      const value = displayFnWrapper(record, options, yearRenoArg);
      return Array.isArray(value) ? value.join(', ') : value;
    },
  },
  400: {
    // 400 - Location/Transport (description) (somehow, this is 353 in shortlist/longlist)
    displayFn: (record: ReportRecord, _, locationDescriptionArg) => {
      const charLimit = 190;
      const descriptionValue: string = (mapAndFormatDynamicValue(record, locationDescriptionArg) as string) || '-';
      return descriptionValue.substring(0, charLimit);
    },
  },
  405: {
    // 405 - map image
    displayFn: (record: ReportRecord, options) => {
      const { reportId } = options;
      // NOTE: to get map for availabilities, we need to use the property id
      const { entity } = record.associatedProperty || { entity: null };
      const edpPropertyRecordId = entity != null ? entity.id : null;
      // NOTE: casting type as reportID & edpRecordId are required in number type
      return of([reportId, edpPropertyRecordId]) as unknown as Observable<string[]>;
    },
  },
  407: {
    displayFn: (_, options) => {
      return getDisclaimerByLocale(options.locale);
    },
  },
  408: {
    // 408 - Floor Number
    displayFn: (record: ReportRecord, _, floorNumberArg) => {
      return mapAndFormatDynamicValue(record, floorNumberArg, undefined, undefined, false);
    },
  },
  409: {
    // 409 - Floor Quality of Space
    displayFn: (record: ReportRecord, _options, floorQuality) => {
      return floorQuality.lovValues.pipe(
        map((lovs: LovValues[]) => {
          const lovKeyValues: string[] = mapAndFormatDynamicValue(
            record,
            floorQuality,
            lovs,
            undefined,
            false
          ) as string[];
          return (lovKeyValues || []).map(lovKeyValue => {
            const lovValue = lovs.find(a => a.key === lovKeyValue);
            return lovValue ? lovValue.value : '-';
          });
        })
      );
    },
  },
  410: {
    // 410 - Number of Workplaces
    displayFn: (record: ReportRecord, _options, numWorkspaceArg) => {
      const availabilities = record.data;
      return availabilities.map(availability => {
        const recordByAvailabilities = { ...record, data: availability };
        const val = mapAndFormatDynamicValue(recordByAvailabilities, numWorkspaceArg);
        return val == null || val === '' ? '-' : val;
      });
    },
  },
  411: {
    // 411 - Available Space
    displayFn: (record: ReportRecord, _, availableSpace) => {
      const availableSpaceValues = mapAndFormatDynamicValue(record, availableSpace, undefined, undefined, false);
      return [].concat(...availableSpaceValues);
    },
  },
  412: {
    // 412 - Date Available
    displayFn: (record: ReportRecord, options, dateAvailable) => {
      const availabilities = record.data || [];
      return availabilities.map(availability => {
        const date = digger(availability, dateAvailable.field);
        const availabilityDate = moment(date, UTC_EDP_INPUT_FORMAT);
        const isValid = availabilityDate.isValid();

        if (!isValid) {
          return '-';
        }

        let isTodayOrBefore = false;
        if (isValid) {
          const now = moment();
          const isSameYear = availabilityDate.isSame(now, 'year');
          const isSameMonth = availabilityDate.isSame(now, 'month');
          const isSameDay = availabilityDate.isSame(now, 'day');
          const isBefore = availabilityDate.isBefore(now);
          isTodayOrBefore = (isSameYear && isSameMonth && isSameDay) || isBefore;
        }

        return isTodayOrBefore ? immediatelyTranslation(options.locale) : availabilityDate.format('[Q]Q YYYY');
      });
    },
  },
  413: {
    // 413 - Lease Type
    displayFn: (record: ReportRecord, _options, leaseTypeArg) => {
      return leaseTypeArg.lovValues.pipe(
        map((lovs: LovValues[]) => {
          const lovKeyValues: string[] = mapAndFormatDynamicValue(
            record,
            leaseTypeArg,
            lovs,
            undefined,
            false
          ) as string[];
          return (lovKeyValues || []).map(lovKeyValue => {
            const lovValue = lovs.find(a => a.key === lovKeyValue);
            return lovValue ? lovValue.value : '-';
          });
        })
      );
    },
  },
  414: {
    // 414 - Space Usage Type
    displayFn: (record: ReportRecord, _options, spaceUsageTypeArg) => {
      return spaceUsageTypeArg.lovValues.pipe(
        map((lovs: LovValues[]) => {
          const lovKeyValues: string[] = mapAndFormatDynamicValue(
            record,
            spaceUsageTypeArg,
            lovs,
            undefined,
            false
          ) as string[];
          return (lovKeyValues || []).map(lovKeyValue => {
            const lovValue = lovs.find(a => a.key === lovKeyValue);
            return lovValue ? lovValue.value : '-';
          });
        })
      );
    },
  },
  415: {
    // 415 - Asking Rent
    displayFn: (record: ReportRecord, options, askingRentArg) => {
      return currencyLov(options).pipe(
        map((lovs: LovValues[]) => {
          const availabilities = record.data || [];
          let newAskingRentArg = askingRentArg;
          if (askingRentArg.uomArgument != null) {
            const uomArgument = { ...askingRentArg.uomArgument, isPrefix: true };
            newAskingRentArg = { ...askingRentArg, uomArgument };
          }
          return availabilities.map(availability => {
            const recordByAvailabilities = { ...record, data: availability };
            const value = mapAndFormatDynamicValue(recordByAvailabilities, newAskingRentArg, lovs, true);

            if (Array.isArray(value)) {
              return value.join(', ');
            }

            return value != null ? value : '-';
          });
        })
      );
    },
  },
  416: {
    // 416 - Availability Description
    displayFn: (record: ReportRecord, _options, availDescriptionArg): string[] => {
      if (availDescriptionArg.field == null) {
        return ['-'];
      }
      const availableSpaceValues = mapAndFormatDynamicValue(
        record,
        availDescriptionArg,
        undefined,
        undefined,
        false
      ) as string[];
      return Array.isArray(availableSpaceValues) ? [].concat(...availableSpaceValues) : availableSpaceValues;
    },
  },
  390: {
    // 390 - landlord
    displayFn: (record: ReportRecord) => {
      const officeUsagesPath = `usages[ref_property_usage_type_desc='Office']`;
      const legalOwnerPath = `company_contact_role_addresses[role_desc='Legal Owner'].party_organization_display_name`;
      const fullPath = `${officeUsagesPath}.${legalOwnerPath}`;
      const landlordValue = digger(record.associatedProperty, fullPath);
      return simpleValueValidation(landlordValue) ? landlordValue : '-';
    },
  },
  526: {
    // 526 - property details label
    displayFn: displayFnWrapper,
  },
  527: {
    /*
     * NOTE:(edmundo): we were supposed to use 98 & 99 for the availability images
     * and 528 & 527 for property images. However, due to existing images logic
     * implemented in the report-field-formatter.service, we cannot use 98 & 99 to
     * return the custom data and data structure we need for each availabilities.
     *
     * Therefore, 98 & 99 will be used for property images & 528 & 527 for the
     * availabilities images
     */
    // 527 - Property Additional Image
    displayFn: (record: ReportRecord, options) => {
      const { urlPrefix, urlSplitter } = options;
      const availabilities = record.data;
      return (availabilities || []).map(availability => {
        const availabilityMedia = availability.getMediaInformation() || [];
        const availabilityPrimary = getPrimaryImage(availabilityMedia);
        const availabilityMediaWithNoPrimary = availabilityMedia.filter(media => {
          return isMediaImage(media) && availabilityPrimary != null && media.id !== availabilityPrimary.id;
        });

        let propertyMediaWithNoPrimary = [];
        if (availabilityMediaWithNoPrimary.lenght === 0) {
          const propertyMedia = getMediaInformation(record);
          const propertyPrimary = getPrimaryImage(propertyMedia);
          propertyMediaWithNoPrimary = propertyMedia.filter(media => {
            return isMediaImage(media) && propertyPrimary != null && media.id !== propertyPrimary.id;
          });
        }
        const settings = { urlPrefix, urlSplitter, onlyUrl: true };
        return getPrimarySecureImage(availabilityMediaWithNoPrimary, propertyMediaWithNoPrimary, settings);
      });
    },
  },
  528: {
    // 528 - Property Primary Image
    displayFn: (record: ReportRecord, options) => {
      const { urlPrefix, urlSplitter } = options;
      const availabilities = record.data;
      return (availabilities || []).map(availability => {
        const availabilityMedia = availability.getMediaInformation();
        const propertyMedia = getMediaInformation(record);
        const settings = { urlPrefix, urlSplitter, onlyUrl: true };
        return getPrimarySecureImage(availabilityMedia, propertyMedia, settings);
      });
    },
  },
  529: {
    // 529 - Location Transport List
    displayFn: (record: ReportRecord, _, _amenityDescription, _amenityDistance, amenityDistanceUOM) => {
      return amenityDistanceUOM.lovValues.pipe(
        map((amenityDistanceUOMLov: LovValues[]) => {
          const AMENITIES_TRANSPORT_TYPE = 'Transportation';
          const transportAmenities = digger(record.associatedProperty, 'property_amenity') || [];
          const mappedAmenity = transportAmenities.map(amenity => {
            const {
              amenity_distance_from_property,
              amenity_distance_from_property_uom,
              property_amenity_type_desc,
              amenity_type_desc,
            } = amenity;
            if (amenity_type_desc !== AMENITIES_TRANSPORT_TYPE) {
              return null;
            }
            const mappedType = getRegularOrColloquialVal(amenity_distance_from_property_uom, amenityDistanceUOMLov);
            // NOTE: abbreviation mappedType lov is missing
            // NOTE2: amenity_distance_from_property is missing an lov due to wrong mapping from Jonathan in the requirement.
            return `${amenity_distance_from_property} ${mappedType} ${property_amenity_type_desc}`;
          });
          // show max of 4 items
          const filteredAmenities = mappedAmenity.filter(amenity => amenity != null);
          const max = filteredAmenities.length < 4 ? filteredAmenities.length : 4;
          return filteredAmenities.slice(0, max);
        })
      );
    },
  },
  542: {
    // 542 - Availability Floor Plan
    displayFn: (record: ReportRecord, options) => {
      const availabilities = record.data;
      const { urlPrefix, urlSplitter } = options;
      return availabilities.map(availability => {
        const availabilityMedia = availability.getMediaInformation() || [];
        const propertyMedia = getMediaInformation(record);
        const floorPlan = getImageByType(availabilityMedia);
        const altFloorPlan = floorPlan == null ? getImageByType(propertyMedia) : floorPlan;
        return genThumbMediaURI(urlSplitter, urlPrefix, altFloorPlan != null ? altFloorPlan.media_path : null, {
          provideDefault: true,
        });
      });
    },
  },
  530: {
    // 530 - selected properties label
    displayFn: displayFnWrapper,
  },
  539: {
    // 539 - Availability Address
    displayFn: (record: ReportRecord, options, ...args) => {
      const { displayFnArgumentsOrder } = options;
      const finalArgs = sortAttributesFields(args, displayFnArgumentsOrder);
      const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      const propertyAddress = concatValuesBy(', ', ...values);
      return (record.data || []).map(() => {
        return propertyAddress;
      });
    },
  },
  540: {
    // 540 - Availability Property Name
    displayFn: (record: ReportRecord) => {
      const propertyName = digger(record.associatedProperty, 'property_name') || '-';
      const availabilities = record.data;
      return availabilities.map(() => propertyName);
    },
  },
  541: {
    // 541 - Floor Details (Label)
    displayFn: (record: ReportRecord) => {
      /*
       * NOTE: this particular function returns an array of strings, even though
       * the value will not be displayed anywhere. However, the array will be used
       * by the `multiple-label` data type handler (in the reporting server)
       * to identify the number of labels required
       */
      const availabilities = record.data;
      return availabilities.map(() => '-');
    },
  },
};
